import React, { useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider,
    Chip,
    IconButton,
} from '@mui/material';
import { Close as CloseIcon, Person as PersonIcon } from '@mui/icons-material';
import timesheetService from '../../../services/timesheetService';

function formatDateUS(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

function formatDateTimeUS(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    let hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const mm = minutes < 10 ? `0${minutes}` : minutes;
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()} ${hours}:${mm} ${ampm}`;
}

function getWeekdayName(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
}

export default function EmployeeOtherHoursModal({
    open,
    onClose,
    employeeName,
    weekStart,
    weekEnd,
    records,
    jobs,
}) {
    const totalHours = useMemo(
        () => (records || []).reduce((sum, r) => sum + (r.hours || 0), 0),
        [records]
    );

    const grouped = useMemo(() => {
        const map = new Map();
        for (const r of (records || [])) {
            const key = r.workDate;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(r);
        }
        return [...map.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, list]) => ({
                date,
                totalHours: list.reduce((s, x) => s + (x.hours || 0), 0),
                items: list.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)),
            }));
    }, [records]);

    const jobById = useMemo(() => {
        const map = new Map();
        for (const j of (jobs || [])) map.set(j.id, j);
        return map;
    }, [jobs]);

    const getJobLabel = (rec) => {
        const code = rec.jobCode || jobById.get(rec.jobId)?.Job || '';
        const name = rec.jobName || timesheetService.getJobName(jobById.get(rec.jobId)) || '';
        return { code, name };
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                <PersonIcon color="primary" />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                        {employeeName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Hours from other foremen · {formatDateUS(weekStart)} — {formatDateUS(weekEnd)}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box
                    sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 1.5, py: 1, mb: 1.5, borderRadius: 1,
                        bgcolor: '#fff7ed', border: '1px solid #fed7aa',
                    }}
                >
                    <Typography variant="body2" sx={{ color: '#9a3412', fontWeight: 600 }}>
                        Total recorded by others
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#c2410c', fontWeight: 700 }}>
                        {totalHours}h
                    </Typography>
                </Box>

                {grouped.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            No hours recorded by other foremen.
                        </Typography>
                    </Box>
                ) : (
                    grouped.map(({ date, totalHours: dayTotal, items }) => (
                        <Box key={date} sx={{ mb: 1.5 }}>
                            <Box sx={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                px: 1, py: 0.5, bgcolor: 'grey.100', borderRadius: '4px 4px 0 0',
                                borderBottom: '1px solid #d0d0d0',
                            }}>
                                <Typography variant="body2" fontWeight={600}>
                                    {getWeekdayName(date)} · {formatDateUS(date)}
                                </Typography>
                                <Chip
                                    label={`${dayTotal}h`}
                                    size="small"
                                    sx={{ bgcolor: '#fed7aa', color: '#9a3412', fontWeight: 700, height: 20 }}
                                />
                            </Box>
                            <Box sx={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                                {items.map((rec, idx) => {
                                    const { code, name } = getJobLabel(rec);
                                    return (
                                        <Box
                                            key={rec.clientId || idx}
                                            sx={{
                                                display: 'flex', alignItems: 'center',
                                                px: 1.25, py: 0.75,
                                                borderBottom: idx < items.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                gap: 1,
                                            }}
                                        >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 700, color: '#1976d2', fontSize: '0.85rem' }}
                                                >
                                                    {code ? `#${code}` : '—'}
                                                </Typography>
                                                {name && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            display: 'block',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {name}
                                                    </Typography>
                                                )}
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: '#64748b', display: 'block', lineHeight: 1.3, mt: 0.25 }}
                                                >
                                                    By <strong>{rec.createdByName || 'Unknown foreman'}</strong>
                                                    {rec.createdAt && ` · ${formatDateTimeUS(rec.createdAt)}`}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 700, color: '#c2410c', fontSize: '0.95rem', flexShrink: 0 }}
                                            >
                                                {rec.hours}h
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
